import { Box, Button, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { ApiMessageStack, Input } from "components";
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";

const MAX_MRI_NEGOTIATOR_EMAIL_LENGTH = 255;

interface IProps {
  accountStore: UserAccountStore;
  isManuallyTriggered: boolean;
}

export const MRIRadarIntegrationDefaultNegotiatorConfigurator: FC<IProps> = observer(
  ({ accountStore, isManuallyTriggered }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const accountIntegrationMRIRadarNegotiatorsStore =
      accountStore.accountIntegrationsStore.accountIntegrationMRIRadarStore
        .accountIntegrationMRIRadarNegotiatorsStore;
    const defaultMriNegotiatorEmail =
      accountIntegrationMRIRadarNegotiatorsStore.mriRadarDefaultNegotiatorEmail;

    const [localValue, setLocalValue] = useState(
      defaultMriNegotiatorEmail ?? ""
    );

    useEffect(() => {
      setLocalValue(defaultMriNegotiatorEmail ?? "");
    }, [defaultMriNegotiatorEmail]);

    const normalizedValue = localValue.trim() || null;
    const isUnchanged = normalizedValue === defaultMriNegotiatorEmail;

    const submitDefaultNegotiator = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationMRIRadarNegotiatorsStore.updateMRIRadarDefaultNegotiator(
          localValue.trim() || null
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={"MRI Radar default negotiator updated."}
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [localValue, accountIntegrationMRIRadarNegotiatorsStore, toast]);

    return (
      <IntegrationConfiguratorLayout disabled={isManuallyTriggered}>
        <Box fontSize={"xl"}>Default negotiator</Box>
        <Box fontSize={"md"} color={"leadpro.400"}>
          When MRI Radar syncs automatically, this MRI login email is the
          filenote Owner for any lead without an assigned, mapped negotiator.
        </Box>
        <Input
          value={localValue}
          placeholder={"Enter MRI login email"}
          aria-label={"Default MRI login email"}
          maxLength={MAX_MRI_NEGOTIATOR_EMAIL_LENGTH}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setLocalValue(e.target.value)
          }
          onBlur={(e: ChangeEvent<HTMLInputElement>) =>
            setLocalValue(e.target.value.trim())
          }
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitDefaultNegotiator}
            isLoading={isSubmitting}
            disabled={isManuallyTriggered || isUnchanged}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
